import { createContext, useContext, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { fmtNum, fmtUSD, fmtPct } from "@/lib/format";
import { Lock, Eye, EyeOff, Mail, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const GateCtx = createContext(null);
export const useGate = () => useContext(GateCtx);

// Metric formatter (mirrors Home.jsx) so the gate teaser renders values consistently.
function fmtMetric(v, kind) {
  if (v == null) return <span className="text-muted-foreground text-sm">—</span>;
  if (kind === "pct") return `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
  if (kind === "usd") return `${v < 0 ? "-" : ""}$${Math.abs(Number(v)).toLocaleString(undefined, {maximumFractionDigits: 2})}`;
  if (kind === "int") return Math.round(Number(v)).toLocaleString();
  if (kind === "days") { const n = Math.max(0, Math.round(Number(v))); return `${n.toLocaleString()} d`; }
  return Number(v).toFixed(2);
}
function metricTone(key, v) {
  if (v == null) return "";
  if (["total_pnl", "gain_pct", "expectancy", "sharpe", "sortino", "calmar", "profit_factor"].includes(key))
    return Number(v) >= 0 ? "pos" : "neg";
  if (key === "max_drawdown_pct") return "neg";
  return "";
}

export function GateProvider({ children }) {
  const [passed, setPassed] = useState(false);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [teaser, setTeaser] = useState(null);
  const [mode, setMode] = useState("pw"); // "pw" | "request" | "submitted"
  const [req, setReq] = useState({ email: "", name: "", note: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("piplight_gate") === "ok") setPassed(true);
    else api.get("/gate/teaser").then((r) => setTeaser(r.data)).catch(() => setTeaser(null));
  }, []);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/gate", { password: pw });
      localStorage.setItem("piplight_gate", "ok");
      setPassed(true);
    } catch { setErr("Incorrect or expired preview code."); }
  }

  async function submitRequest(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/gate/access-request", req);
      setMode("submitted");
    } catch (er) { toast.error(formatErr(er)); }
    finally { setSubmitting(false); }
  }

  if (passed) return <GateCtx.Provider value={{ passed }}>{children}</GateCtx.Provider>;

  const funds = teaser?.funds || [];
  return (
    <div className="min-h-screen relative grain">
      <div className="container-tight py-12 md:py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* LEFT: gate + access request */}
          <div className="lg:col-span-5">
            <div className="surface p-8 md:p-10 sticky top-8">
              <div className="kicker mb-4">Private Preview</div>
              <div className="flex items-center gap-3 mb-6">
                <Lock strokeWidth={1.4} size={22} className="gold" />
                <h1 className="text-3xl md:text-4xl">Preview Access</h1>
              </div>

              {mode === "pw" && (
                <>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    The Piplight Capital platform is in private preview. Enter your preview code below.
                  </p>
                  <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
                    <span className="gold">Staff & existing clients:</span> after entering the code, tap
                    <em> Client Login</em> in the top-right corner to sign in.
                  </p>
                  <form onSubmit={submit} className="space-y-3">
                    <div className="relative">
                      <input
                        data-testid="gate-password-input"
                        type={showPw ? "text" : "password"}
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        className="w-full bg-secondary border border-border px-4 py-3 pr-12 outline-none focus:border-primary text-sm"
                        placeholder="Preview code (e.g. PL-XXXXXXXX)"
                        autoFocus
                      />
                      <button type="button" onClick={() => setShowPw((s) => !s)}
                        data-testid="gate-toggle-pw" tabIndex={-1}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPw ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                      </button>
                    </div>
                    {err && <div data-testid="gate-error" className="text-sm neg">{err}</div>}
                    <button data-testid="gate-submit" type="submit" className="btn-gold w-full py-3 text-sm uppercase tracking-widest">
                      Enter Preview
                    </button>
                  </form>
                  <div className="mt-6 pt-6 border-t border-border/60">
                    <div className="text-xs text-muted-foreground mb-2">Don't have a preview code?</div>
                    <button data-testid="gate-request-open" onClick={() => setMode("request")}
                      className="w-full border border-border hover:border-primary px-4 py-2.5 text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                      <Mail size={14} strokeWidth={1.5} /> Request Access
                    </button>
                  </div>
                </>
              )}

              {mode === "request" && (
                <>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    Leave your details and we will email you a time-limited preview code.
                  </p>
                  <form onSubmit={submitRequest} className="space-y-3" data-testid="gate-request-form">
                    <input required type="email" placeholder="Email address"
                      data-testid="gate-req-email"
                      value={req.email} onChange={(e) => setReq({ ...req, email: e.target.value })}
                      className="w-full bg-secondary border border-border px-4 py-3 outline-none focus:border-primary text-sm" />
                    <input placeholder="Name (optional)"
                      data-testid="gate-req-name"
                      value={req.name} onChange={(e) => setReq({ ...req, name: e.target.value })}
                      className="w-full bg-secondary border border-border px-4 py-3 outline-none focus:border-primary text-sm" />
                    <textarea rows={3} placeholder="Note — how did you find us, what are you looking for? (optional)"
                      data-testid="gate-req-note"
                      value={req.note} onChange={(e) => setReq({ ...req, note: e.target.value })}
                      className="w-full bg-secondary border border-border px-4 py-3 outline-none focus:border-primary text-sm" />
                    <button type="submit" disabled={submitting}
                      data-testid="gate-request-submit"
                      className="btn-gold w-full py-3 text-sm uppercase tracking-widest disabled:opacity-60">
                      {submitting ? "Sending…" : "Request Preview Code"}
                    </button>
                    <button type="button" onClick={() => setMode("pw")}
                      className="w-full text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground py-1">
                      Back to code entry
                    </button>
                  </form>
                </>
              )}

              {mode === "submitted" && (
                <div className="text-sm leading-relaxed" data-testid="gate-request-thanks">
                  <p className="text-lg gold mb-3">Thank you.</p>
                  <p className="text-muted-foreground">Your request has been forwarded to our investor relations team. If approved, a time-limited preview code will arrive in your inbox shortly.</p>
                  <button onClick={() => { setMode("pw"); setReq({ email: "", name: "", note: "" }); }}
                    className="mt-6 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
                    Enter a code instead →
                  </button>
                </div>
              )}

              <div className="mt-8 kicker text-[10px]">Piplight Capital, LLC — Quantitative Trading Division</div>
            </div>
          </div>

          {/* RIGHT: fund performance teaser */}
          <div className="lg:col-span-7">
            <div className="mb-6">
              <div className="kicker mb-2 flex items-center gap-2">
                <TrendingUp size={12} strokeWidth={1.5} className="gold" /> Live Performance
              </div>
              <h2 className="text-2xl md:text-3xl mb-2">A glimpse of what's inside.</h2>
              <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
                Below are the live institutional metrics for each Piplight fund, streamed from the trading terminals themselves. Full NAV history, drawdown curves, per-client statements, and interactive charts unlock after preview access.
              </p>
            </div>

            <div className="space-y-4" data-testid="gate-teaser-funds">
              {funds.length === 0 && (
                <div className="border border-border p-6 text-sm text-muted-foreground">Live metrics loading…</div>
              )}
              {funds.map((f) => (
                <div key={f.id} className="surface p-5" data-testid={`gate-fund-${f.id}`}>
                  <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                    <div>
                      <div className="kicker text-[10px] mb-1">{f.code} · <span className="uppercase">{f.status}</span></div>
                      <div className="text-lg">{f.name}</div>
                      {f.tagline && <div className="text-xs text-muted-foreground mt-1 max-w-lg">{f.tagline}</div>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(f.instruments || []).slice(0, 4).map((i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 border border-border text-muted-foreground font-mono uppercase tracking-widest">{i}</span>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-border pt-4">
                    {(teaser?.labels || []).map(([lbl, key, kind]) => {
                      const v = f.metrics?.[key];
                      return <Tile key={key} label={lbl} value={fmtMetric(v, kind)} tone={metricTone(key, v)} />;
                    })}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
              Figures shown are live from each fund's MT5 terminal via the PiplightWebhook feed. Full audited NAV series, drawdown curves, and per-client statements are available inside the preview.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const Tile = ({ label, value, tone }) => (
  <div>
    <div className="kicker text-[9px] mb-1">{label}</div>
    <div className={`font-mono text-sm ${tone || ""}`}>{value}</div>
  </div>
);
